import { useDepartmentsApi } from "@/api/departments";
import useProductsApi from "@/api/products";
import Autocomplete from "@/components/ui/autocomplete";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  Carousel as ProductCarousel,
} from "@/components/ui/carousel";
import ProductCard from "@/components/ui/product-card";
import { Categories } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { MERCHANDISED_DEPARTMENTS, PRODUCTS } from "@/data/query-keys";
import useProducts from "@/hooks/useProducts";
import { DSS_URL } from "@/lib/config";
import { useQuery } from "@tanstack/react-query";
import BackToUp from "@uiw/react-back-to-top";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  IoArrowUpSharp,
  IoChevronDown,
  IoGridSharp,
  IoListSharp,
} from "react-icons/io5";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMediaQuery } from "react-responsive";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";
import Toggle from "react-toggle";
import "react-toggle/style.css";
import { updateInLocation } from "serialize-query-params";

// import Modal from "@/components/ui/content-modal";
import SectionProducts from "@/components/ui/section-products";
import useGlobal from "@/hooks/useGlobal";
import { getCorridor } from "@/lib/utils";
import React from "react";
import DesktopBanner from "./components/DesktopBanner";

const Banners = React.lazy(() => import("./components/banners"));

const useHomeData = () => {
  const params = useParams<any>();
  const { fetchMerchandisedDepartments } = useDepartmentsApi();
  const [searchParams] = useSearchParams();
  const {
    data: categories,
    isLoading,
    error,
  } = useQuery({
    queryKey: [MERCHANDISED_DEPARTMENTS],
    queryFn: fetchMerchandisedDepartments,
    refetchOnWindowFocus: false,
  });

  const { fetchAllProducts } = useProductsApi();
  const { setAllProducts } = useProducts();

  const options: any = {};

  // const [options, setOptions] = useState<any>({});

  if (params?.filter !== undefined)
    options.filter = "Promotions:" + params.filter;

  if (params?.context !== undefined) options.context = params.page;

  if (params?.search_query !== undefined) options.qsearch = params.search_query;

  if (params?.department !== undefined)
    options.department_slug = params.department;

  if (params?.category !== undefined && params?.category !== "all")
    options.category_slug = params.category;

  if (params?.featured_type !== undefined)
    options.featured_type = params.featured_type;

  if (searchParams.get("sort")) options.sort = searchParams.get("sort");

  const {
    data: products,
    isLoading: isLoadingProducts,
    isFetching: isFetchingProducts,
    error: productsError,
    refetch,
  } = useQuery({
    queryKey: [PRODUCTS, options],
    queryFn: () =>
      fetchAllProducts(options).then((data) => {
        setAllProducts(data.products);
        return data;
      }),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // keepPreviousData: true,
  });

  return {
    categories,
    isLoading,
    error,
    refetch,
    products,
    isLoadingProducts,
    isFetchingProducts,
    productsError,
  };
};

const PAGE_SIZE = 36; // Define the number of products per page

export default function HomePage() {
  const [searchParams] = useSearchParams();

  const isMobile = useMediaQuery({ maxWidth: 768 });

  // const sortParam = searchParams.get("sort");

  const location: any = useLocation();
  const navigate = useNavigate();
  // const [next, setNext] = useState<string | null>(null);
  const { next, setNext, resultsFound, setResultsFound } = useProducts();
  // const [resultsFound, setResultsFound] = useState<string | null>(null);

  const params = useParams<any>();

  const { isLoading, products, isFetchingProducts } = useHomeData();

  // const [allProducts, setAllProducts] = useState<any>([]);

  const { allProducts, setAllProducts } = useProducts();

  useEffect(() => {
    if (products) {
      // console.log("products changed");
      setNext(products.next_page);
      setResultsFound(products.num_found_text);
      // if (allProducts.length == 0) setAllProducts(products.products);
      // setAllProducts(products.products);
    }
  }, [products]);

  const [isLoadingMoreProducts, setIsLoadingMoreProducts] =
    useState<boolean>(false);

  const fetchMoreProducts = async () => {
    setIsLoadingMoreProducts(true);
    const options: any = {};

    if (params?.filter !== undefined)
      options.filter = "Promotions:" + params.filter;
    if (params?.context !== undefined) options.context = params.page;
    if (params?.search_query !== undefined)
      options.qsearch = params.search_query;

    if (params?.department !== undefined)
      options.department_slug = params.department;

    if (params?.category !== undefined && params?.category !== "all")
      options.category_slug = params.category;

    const {
      after,
      before,
      context,
      filter,
      qsearch,
      department_slug,
      category_slug,
    } = options;

    const queryParams = [];
    if (after) queryParams.push(`after=${after}`);
    if (before) queryParams.push(`before=${before}`);
    if (context) queryParams.push(`context=${context}`);
    if (filter) queryParams.push(`filter=${filter}`);
    if (qsearch) queryParams.push(`qsearch=${qsearch}`);
    if (department_slug) queryParams.push(`department_slug=${department_slug}`);
    if (category_slug) queryParams.push(`category_slug=${category_slug}`);
    if (next) queryParams.push(`after=${next}`);

    // Construct the query string by joining all parameters with '&'
    const queryString =
      queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

    // console.log("queryString", queryString);

    await fetch(`${DSS_URL}/products${queryString}`)
      .then((response) => response.json())
      .then((data) => {
        // console.log(data);
        setNext(data.next_page);
        setResultsFound(data.num_found_text);
        // setAllProducts((prevProducts: any) => [
        // 	...prevProducts,
        // 	...data.products,
        // ]);
        setAllProducts([...allProducts, ...data.products]);
        setIsLoadingMoreProducts(false);
      })
      .catch((error) => console.error("Error:", error))
      .finally(() => {
        setIsLoadingMoreProducts(false);
      });
  };

  const [relatedSearches, setRelatedSearches] = useState([]);

  const fetchRelatedSearches = async () => {
    fetch(
      `${DSS_URL}/related-searches?qsearch=${encodeURIComponent(
        params?.search_query
      )}`
    )
      .then((res) => res.json())
      .then((data) => {
        // console.log("related searches", data);
        setRelatedSearches(data.sections.related_searches.results);
      });
  };

  useEffect(() => {
    if (params?.search_query) fetchRelatedSearches();
    else setRelatedSearches([]);
  }, [params]);

  const {
    autoScroll,
    setAutoScroll,
    openInNewTab,
    setOpenInNewTab,
    showGrid,
    setShowGrid,
  } = useGlobal();

  useEffect(() => {
    // if (!isMobile) {
    // 	setAutoScroll(false);
    // }
    if (isMobile) {
      setOpenInNewTab(false);
    }
  }, []);



  useEffect(() => {
      const addCartMetaTags = () => {
        document
          .querySelectorAll('meta[property^="og:product:"]')
          .forEach((el) => el.remove());

        if (allProducts.length > 0) {

          let metaCartContents = document.querySelector(
            'meta[property="og:cart_contents"]'
          );
          if (!metaCartContents) {
            metaCartContents = document.createElement("meta");
            metaCartContents.setAttribute("property", "og:cart_contents");
            document.head.appendChild(metaCartContents);
          }
          metaCartContents.setAttribute(
            "content",
            allProducts.map((item) => `PLID${item.sku}`).join(",")
          );

          allProducts.forEach((item, index) => {
            const metaTags = [
              { property: `og:product:id:${index}`, content: `PLID${item.sku}` },
              { property: `og:product:title:${index}`, content: item.title },
              {
                property: `og:product:price:amount:${index}`,
                content: Number(item.price).toString(),
              },
              {
                property: `og:product:price:currency:${index}`,
                content: item.currency,
              },
              {
                property: `og:product:availability:${index}`,
                content: item.delivery_days > 0 ? "in stock" : "out of stock",
              },
              { property: `og:product:image:${index}`, content: item.image },
            ];

            metaTags.forEach((tagData) => {
              const meta = document.createElement("meta");
              meta.setAttribute("property", tagData.property);
              meta.setAttribute("content", tagData.content);
              document.head.appendChild(meta);
            });
          });
        }
      };

      addCartMetaTags();


      return () => {
        document
          .querySelectorAll('meta[property^="og:product:"]')
          .forEach((el) => el.remove());
        document.querySelector('meta[property="og:cart_contents"]')?.remove();
      };
    }, [allProducts]);

  const renderRelatedSearches = (black) => {
    return (
      <React.Fragment>
        {relatedSearches.length > 0 && (
          <div className="my-4 flex items-center gap-4 w-full">
            <ProductCarousel
              opts={{
                align: "start",
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {relatedSearches.map((item, key) => (
                  <CarouselItem
                    key={key}
                    className="pl-4 basis-auto lg:basis-auto flex items-center select-none"
                  >
                    <span
                      onClick={() =>
                        navigate(`/search/${item.related_search.qsearch}`)
                      }
                      className={` ${
                        black
                          ? "bg-black hover:bg-red-500"
                          : "bg-red-500 hover:bg-black"
                      } cursor-pointer rounded-sm  p-2 px-3 text-xs text-white`}
                    >
                      {item.related_search.qsearch}
                    </span>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="-ml-1 rounded-none md:rounded-tl-xs md:rounded-bl-xs md:ml-2" />
              <CarouselNext className="-mr-1 rounded-none md:rounded-tr-xs md:rounded-br-xs md:mr-3" />
            </ProductCarousel>
            {/* <div className="flex flex-wrap gap-2 overflow-hidden">
									{relatedSearches.map((s, k) => (
										<span
											onClick={() =>
												navigate(
													`/search/${s.related_search.qsearch}`
												)
											}
											key={k}
											className="hover:bg-primary cursor-pointer rounded-3xl bg-gray-800 p-2 px-3 text-xs text-white"
										>
											{s.related_search.qsearch}
										</span>
									))}
								</div> */}
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderProductCards = (horizontal: boolean) => {
    return (
      <React.Fragment>
        {allProducts.map((product: any, index: number) => (
          <React.Fragment key={index}>
            <div className="w-full">
              <ProductCard
                horizontal={horizontal}
                openInNewTab={isMobile ? false : openInNewTab}
                sku={product.sku}
                title={product.title}
                subtitle={product.subtitle}
                brand={product.brand}
                rating={product.rating}
                reviews={product.reviews}
                image={product.image}
                currency={product.price.currency}
                selling_price={product.price.selling}
                listing_price={product.price.was}
                original={product.price.original}
                surcharge={product.price.surcharge}
                markup={product.price.markup}
                delivery_days={product.delivery_days}
                next_delivery_date={product.next_delivery_date}
                is_add_to_cart_available={product.is_add_to_cart_available}
                stock_available={product.stock_availability}
                badges={product.badges}
                quantity={product.quantity}
                variants={product.variants}
              />
            </div>
            {/* Conditionally render full-width row after every 36 products */}
            {(index + 1) % PAGE_SIZE === 0 && relatedSearches.length > 0 && (
              <div className="col-span-full sm:bg-white bg-transparent sm:p-3 pt-4 rounded-none sm:rounded-lg sm:border s;:border-[#E8E8E8]">
                <span className="text-gray-800 text-xs px-2 font-medium md:px-0 md:text-sm">
                  Can't find what you're looking for? Try these related
                  keywords:
                </span>
                {renderRelatedSearches(false)}
              </div>
            )}
          </React.Fragment>
        ))}
      </React.Fragment>
    );
  };

  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowBanner(true);
    }, 5000); // Adjust the delay as needed

    return () => clearTimeout(timer);
  }, []);

  return (
    <div  className="w-full flex flex-col sm:gap-8 mb-12">
      <Helmet>
        <title>
          Daily Sale Shop - 12 million products delivered to your doorstep.
        </title>
      </Helmet>
      {/* banner section */}
      {/* BANNERS HERE */}
      {/* <div className="w-full flex justify-center my-0 sm:my-4 sm:mb-0"> */}
      {/* {showBanner && ( */}
      {/* <Suspense fallback={<p className="my-4">Loading...</p>}> */}
      <div className="flex grid-container">
        <Banners />
      </div>
      {/* </Suspense> */}
      {/* )} */}
      {/* </div> */}

      <div className="flex flex-row w-full sm:gap-4 grid-container">
        <div className="hidden lg:block w-1/6 h-auto flex-none ">
          {/* sidebar */}
          <div className="sticky top-24">
            <Categories />
          </div>
        </div>

        <div className="flex flex-col w-full">
          {/* content */}
          <div className="flex sticky sm:relative top-[60px] z-[99] sm:z-auto sm:top-0">
            <Autocomplete
              value={params?.search_query ?? ""}
              fetchSuggestions={(input) =>
                fetch(`${DSS_URL}/autocomplete/${encodeURIComponent(input)}`)
                  .then((res) => res.json())
                  .then((data) => {
                    return data;
                  })
              }
              onSearch={(e: any) => {
                navigate(e.length > 0 ? `/search/${e}` : "/");
              }}
            />
          </div>
          <div className="lg:flex w-full">{renderRelatedSearches(true)}</div>

          {resultsFound && (
            <div className="sm:flex justify-between items-center px-2 sm:px-0 mb-2 sm:mb-4 my-4">
              <div className=" text-[#4d4d4f] hidden lg:block font-bold text-xs sm:text-sm">
                {location.pathname === "/" ? "" : resultsFound}
              </div>
              <div className="flex text-sm justify-between lg:justify-center items-center gap-4">
                <div className=" justify-center hidden lg:flex items-center gap-2">
                  <span id="biscuit-label" className="hidden sm:block">
                    Open products in new tab
                  </span>
                  <Toggle
                    id="biscuit-status"
                    // defaultChecked={
                    // 	isMobile ? false : openInNewTab
                    // }
                    checked={false}
                    aria-labelledby="biscuit-label"
                    onChange={() => setOpenInNewTab(false)}
                  />
                </div>

                <div className=" justify-center hidden lg:flex items-center gap-2">
                  <span id="biscuit-label" className="hidden sm:block">
                    Infinity Scroll
                  </span>
                  <Toggle
                    id="biscuit-status"
                    // defaultChecked={autoScroll}
                    checked={autoScroll}
                    aria-labelledby="biscuit-label"
                    onChange={() => setAutoScroll(!autoScroll)}
                  />
                </div>

                <div className="flex justify-center items-center gap-2">
                  <span id="biscuit-label" className="">
                    Sort By
                  </span>

                  <div className="relative inline-block text-left">
                    <select
                      onChange={(e) => {
                        const newLocation = updateInLocation(
                          {
                            sort: e.target.value,
                          },
                          location
                        );

                        navigate(newLocation);
                      }}
                      defaultValue={searchParams.get("sort")}
                      className="block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded-lg shadow leading-tight focus:outline-none focus:shadow-outline"
                    >
                      <option
                        selected={searchParams.get("sort") === "Relevance"}
                        value="Relevance"
                      >
                        Relevance
                      </option>
                      <option
                        selected={
                          searchParams.get("sort") === "Price Descending"
                        }
                        value="Price Descending"
                      >
                        Price: High to Low
                      </option>
                      <option
                        selected={
                          searchParams.get("sort") === "Price Ascending"
                        }
                        value="Price Ascending"
                      >
                        Price: Low to High
                      </option>
                      <option
                        selected={
                          searchParams.get("sort") === "Rating Descending"
                        }
                        value="Rating Descending"
                      >
                        Top Rated
                      </option>
                      <option
                        selected={
                          searchParams.get("sort") === "Newest Arrivals"
                        }
                        value="ReleaseDate Descending"
                      >
                        Newest Arrivals
                      </option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <IoChevronDown />
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <div
                    className={` ${showGrid && "bg-gray-300"} p-1 rounded-xs cursor-pointer duration-500`}
                    onClick={() => setShowGrid(true)}
                  >
                    <IoGridSharp size={25} className="text-gray-600" />
                  </div>
                  <div
                    className={` ${!showGrid && "bg-gray-300"} p-1 rounded-xs cursor-pointer duration-500`}
                    onClick={() => setShowGrid(false)}
                  >
                    <IoListSharp size={25} className="text-gray-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* <div className="flex relative w-96">
						<RelatedSearches />
					</div> */}

          {/* <pre>{JSON.stringify(viewedProducts.length, null, 2)}</pre> */}

          {/* TODO: bring back Pick Up Where you LEft Off */}

          {/* TODO: disable Local Products */}
          {/* {location.pathname === "/" && <FeaturedProducts />} */}

          {location.pathname === "/" && (
            <div className="mb-4 sm:mb-8">

<SectionProducts
                title="MICROWAVE OVENS"
                keyword="microwaves"
                previewItems={[

                  "PLID72170608", "PLID27952679", "PLID70725196", "PLID70725198"

                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_31.png"
              />


              <SectionProducts
                title="DOUBLE DOOR FRIDGES"
                keyword="double door fridges"
                previewItems={[
                  "PLID90628731",
                  "PLID90628732",
                  "PLID71832699",
                  "PLID93338007",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_32.png"
              />



              <SectionProducts
                title="43 INCH TVS"
                keyword="43 inch tvs"
                previewItems={[
                  "PLID95906804",
                  "PLID91299332",
                  "PLID95906804",
                  "PLID95957116",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_36.png"
              />

              <SectionProducts
                title="50 INCH TVS"
                keyword="50 inch tvs"

                previewItems={[
                  "PLID71118321",
                  "PLID95868755",
                  "PLID95110652",
                  "PLID95887846",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_94.png"
              />

              <SectionProducts
                title="65 INCH TVS"
                keyword="65 inch tvs"

                previewItems={[
                  "PLID95800857",
                  "PLID95110641",
                  "PLID95827891",
                  "PLID95776066",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_95.png"
              />

              <SectionProducts
                title="ICE MAKERS"
                keyword="ice makers"

                previewItems={[
                  "PLID96237302",
                  "PLID95774828",
                  "PLID69488018",
                  "PLID93374460",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_96.png"
              />

              <SectionProducts
                title="TOP LOADER WASHING MACHINES"
                keyword="top loader washing machines"

                previewItems={[
                  "PLID72933637",
                  "PLID92830462",
                  "PLID95597726",
                  "PLID96900563",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_98.png"
              />



              <SectionProducts
                title="FRONT LOADER WASHING MACHINES"
                keyword="front loader washing machines"

                previewItems={[
                  "PLID94054208",
                  "PLID90865918",
                  "PLID91485643",
                  "PLID93261916",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_11.png"
              />



<SectionProducts
                title="DISHWASHERS"
                keyword="dishwashers"
                previewItems={[
                  "PLID91855918",
                  "PLID92675429",
                  "PLID92823198",
                  "PLID92823198",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_25.png"
              />


<SectionProducts
                title="OVENS & HOBS"
                keyword="ovens and hobs"

                previewItems={[
                  "PLID91414806",
                  "PLID57054686",
                  "PLID68848531",
                  "PLID68848508",
                ]}
                banner="/banners/mobile/sections/mobile_sectional_banners_92.png"
              />

            </div>
          )}

          {isLoading || isFetchingProducts ? (
            <div className="my-12 flex flex-col gap-4 sm:gap-8 justify-center items-center">
              <PulseLoader
                color={"#CD0F0F"}
                loading={isLoading || isFetchingProducts}
                // cssOverride={override}
                size={20}
                aria-label="Loading Spinner"
              />
              <p className="text-center text-xs sm:text-sm">
                Hang tight while we run a search with our suppliers.
              </p>
            </div>
          ) : (
            <>
              {location.pathname === "/" && (
                <p className="px-2 sm:px-0 text-sm sm:text-xl font-bold my-2">
                  Deal Of The Day
                </p>
              )}{" "}
              <InfiniteScroll
                dataLength={allProducts.length}
                next={fetchMoreProducts}
                hasMore={autoScroll ? (next ? true : false) : false}
                loader={""}
                // endMessage={
                // 	<p style={{ textAlign: "center" }}>
                // 		<b>No more items to load</b>
                // 	</p>
                // }
                className="w-full flex justify-center"
              >
                <div
                  // className="w-full grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:gap-4"
                  className={`w-full ${
                    !showGrid
                      ? "overflow-x-auto flex flex-col sm:gap-2"
                      : "grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 sm:gap-2"
                  }`}
                >
                  {renderProductCards(!showGrid)}
                  {next &&
                    showGrid &&
                    isLoadingMoreProducts && // Show loader divs only when more data is being loaded
                    Array.from(
                      {
                        length: 4 - (allProducts.length % 4),
                      },
                      (_, index) => (
                        <div
                          key={`loader-${index}`}
                          className="max-w-64 w-full cursor-not-allowed overflow-hidden rounded-md border  border-input bg-background bg-white p-2 text-sm font-medium shadow-sm transition-colors  focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                        >
                          <div className="relative mb-2">
                            <Skeleton className="bg-gray-300 h-24 w-full rounded-sm" />
                            <Skeleton className="bg-gray-300 absolute left-2 top-2 h-8 w-8 rounded-sm" />
                            <Skeleton className="bg-gray-300 absolute left-2 top-12 h-8 w-8 rounded-sm" />
                          </div>
                          <div className="space-y-2">
                            <div className="mb-2 flex items-center justify-between">
                              <Skeleton className="bg-gray-300 h-6 w-1/3 " />
                              <Skeleton className="bg-gray-300 h-6 w-1/3" />
                            </div>
                            <Skeleton className="bg-gray-300 h-4 w-full" />
                            <Skeleton className="bg-gray-300 h-3 w-full" />
                            <Skeleton className="bg-gray-300 h-3 w-full" />
                            <div>
                              <Skeleton className="bg-gray-300 flex h-48 w-full flex-col gap-2 px-2 py-8">
                                <div className="mb-2 flex items-center justify-between">
                                  <Skeleton className="bg-gray-300 h-4 w-1/3 " />
                                  <Skeleton className="bg-gray-300 h-4 w-1/3" />
                                </div>
                                <Skeleton className="bg-gray-300 h-8 w-full rounded-sm" />
                                <Skeleton className="bg-gray-300 h-8 w-full rounded-sm" />
                                <Skeleton className="bg-gray-300 mt-4 h-4 w-full" />
                                <Skeleton className="bg-gray-300 h-4 w-full" />
                                <Skeleton className="bg-gray-300 h-4 w-full" />
                              </Skeleton>
                            </div>
                          </div>
                        </div>
                      )
                    )}
                </div>
              </InfiniteScroll>
              {/* <pre>{JSON.stringify(allProducts, null, 2)}</pre> */}
              {allProducts.length > 0 && (
                // {!autoScroll && allProducts.length > 0 && (
                <>
                  {isLoadingMoreProducts ? (
                    <div className="my-12 flex flex-col gap-4 justify-center items-center">
                      <PulseLoader
                        color={"#CD0F0F"}
                        loading={isLoadingMoreProducts}
                        // cssOverride={override}
                        size={20}
                        aria-label="Loading Spinner"
                      />
                      <p className="text-center text-xs sm:text-sm">
                        Hang tight while we run a search with our suppliers.
                      </p>
                    </div>
                  ) : (
                    !autoScroll && (
                      <div className="flex justify-center">
                        <button
                          onClick={() => fetchMoreProducts()}
                          className="border cursor-pointer bg-[#F9F9F9] border-[#E2E2E2] border-solid text-center p-2 px-8 rounded-lg hover:bg-red-500 hover:text-white mx-2 my-2 "
                        >
                          Load More
                        </button>
                      </div>
                    )
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="hidden lg:flex w-1/5 h-auto flex-col gap-4 flex-none ">
          {/* <div
						className="bg-white p-1.5 shadow-md rounded-sm  cursor-pointer"
						onClick={() =>
							(window.location.href = "/categories/computers/all")
						}
					>
						<img src="/today-only/sidebar.png" />
					</div> */}
          {/* <div
						className="bg-white p-1.5 shadow-md rounded-sm  cursor-pointer"
						onClick={() =>
							(window.location.href = "/categories/computers/all")
						}
					>
						<img src="/banners/sidebar/70.png" />
					</div> */}
          <div className=" aspect-[1/1] w-full rounded-sm overflow-hidden">
            <Carousel



            >
              {getCorridor().country === "Zimbabwe" && (
                <img
                  src="/popup/how-to-shop.jpeg"
                  alt="Winner"
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              )}
              {/* <img
								src="/popup/cod.png"
								alt="Winner"
								className="w-full h-full object-contain"
								loading="lazy"
							/> */}
              {/* <img
                src="/winner.jpeg"
                alt="Winner"
                className="w-full h-full object-contain"
                loading="lazy"
              /> */}

              <img
                src={`/popup/${getCorridor().country.toLocaleLowerCase()}.png`}
                alt="Popup"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </Carousel>
          </div>

          {/*<DesktopBanner
						src="/banners/sidebar/desktop/4.png"
						url="https://www.facebook.com/dailysaleshopzw"
					/>*/}

          <DesktopBanner
             src="/banners/sidebar/desktop_sidebar_banners_32.png"
            keyword="travel"
          />

          <DesktopBanner
           src="/banners/sidebar/desktop_sidebar_banners_31.png"
            keyword="gifts"
          />
          <DesktopBanner
            src="/banners/sidebar/desktop_sidebar_banners_37.png"
            keyword="dining"
          />
          <DesktopBanner
            src="/banners/sidebar/desktop_sidebar_banners_2=36.png"
            keyword="kitcheware"
          />

          {/* <div
						className="bg-white shadow-sm rounded-sm p-1.5 cursor-pointer"
						onClick={() => (window.location.href = "/search/defy")}
					>
						<img src="/banners/sidebar/3.png" />
					</div> */}
        </div>
      </div>
      <BackToUp className="hidden sm:block mr-8">
        <IoArrowUpSharp size={15} />
      </BackToUp>
      {/* FIXME: popup affecting performance */}
      {/* <Modal
				visible={displayPopUp}
				onClose={closePopUp}
				content={
					<>
						<div className="">
							<img
								src={`/popup/${getCorridor().country.toLocaleLowerCase()}.png`}
							/>
						</div>
					</>
				}
				special
			/> */}
    </div>
  );
}
